/**
 * WallVolume3D - Renders a wall as a true 3D volume body
 *
 * Uses the new wall data model from useWallState:
 * 8 corner points (4 bottom + 4 top), inner/outer faces, joint-aware geometry.
 *
 * 2D view: shows inner + outer edges as lines, filled polygon between them
 * 3D view: renders all 6 faces of the volume box with openings cut out
 */

import React, { useMemo, useCallback, useState } from 'react';
import { Line, Text } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { WALL_COLORS, SIZES } from '../utils/constants';
import { distance, midpoint, lineAngle, formatLength } from '../utils/geometry';

/**
 * @param {Object} wall - Wall data from useWallState (8-point model)
 * @param {boolean} isSelected
 * @param {boolean} isHovered
 * @param {string} viewMode - '2d' | '3d'
 * @param {Function} onSelect
 * @param {Function} onHover
 * @param {Function} onFlip
 * @param {boolean} showDimensions
 * @param {number} dimensionFontSize
 * @param {string} selectedOpeningId
 * @param {Function} onSelectOpening
 */
export function WallVolume3D({
  wall,
  isSelected = false,
  isHovered = false,
  viewMode = '2d',
  showDimensions = true,
  dimensionFontSize = 40,
  onSelect,
  onHover,
  onFlip,
  // Opening props
  selectedOpeningId = null,
  hoveredOpeningId = null,
  onSelectOpening,
  onHoverOpening,
}) {
  const { camera, gl } = useThree();

  if (!wall || !wall.points) return null;

  const pts = wall.points;

  // Extract 2D bottom points for floor-plan rendering
  const ibs = pts.innerBottomStart;
  const ibe = pts.innerBottomEnd;
  const obs = pts.outerBottomStart;
  const obe = pts.outerBottomEnd;
  const its = pts.innerTopStart;
  const ite = pts.innerTopEnd;
  const ots = pts.outerTopStart;
  const ote = pts.outerTopEnd;

  // Baseline info
  const baseStart = wall.baseStart;
  const baseEnd = wall.baseEnd;
  const length = distance(baseStart, baseEnd);
  const mid = midpoint(baseStart, baseEnd);
  const angle = lineAngle(baseStart, baseEnd);

  // Color
  const getColor = () => {
    if (isSelected) return WALL_COLORS.selected;
    if (isHovered) return WALL_COLORS.hover;
    return WALL_COLORS.exterior;
  };

  // ============= EVENT HANDLERS =============

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect?.(wall.id, e.nativeEvent || e);
  }, [wall.id, onSelect]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.('wall', wall.id);
    document.body.style.cursor = 'pointer';
  }, [wall.id, onHover]);

  const handlePointerOut = useCallback(() => {
    onHover?.(null, null);
    document.body.style.cursor = 'default';
  }, [onHover]);

  // ============= 2D GEOMETRY =============

  // Filled quad between inner and outer edges (2D plan view)
  const wallGeometry2D = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    // Two triangles: (ibs, ibe, obe) and (ibs, obe, obs)
    const vertices = new Float32Array([
      ibs.x, ibs.y, 0.5,  ibe.x, ibe.y, 0.5,  obe.x, obe.y, 0.5,
      ibs.x, ibs.y, 0.5,  obe.x, obe.y, 0.5,  obs.x, obs.y, 0.5,
    ]);
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.computeVertexNormals();
    return geometry;
  }, [ibs.x, ibs.y, ibe.x, ibe.y, obs.x, obs.y, obe.x, obe.y]);

  // Inner edge line points
  const innerLine2D = useMemo(() => [
    new THREE.Vector3(ibs.x, ibs.y, 0),
    new THREE.Vector3(ibe.x, ibe.y, 0),
  ], [ibs.x, ibs.y, ibe.x, ibe.y]);

  // Outer edge line points
  const outerLine2D = useMemo(() => [
    new THREE.Vector3(obs.x, obs.y, 0.5),
    new THREE.Vector3(obe.x, obe.y, 0.5),
  ], [obs.x, obs.y, obe.x, obe.y]);

  // Start cap line
  const startCap2D = useMemo(() => [
    new THREE.Vector3(ibs.x, ibs.y, 0.3),
    new THREE.Vector3(obs.x, obs.y, 0.3),
  ], [ibs.x, ibs.y, obs.x, obs.y]);

  // End cap line
  const endCap2D = useMemo(() => [
    new THREE.Vector3(ibe.x, ibe.y, 0.3),
    new THREE.Vector3(obe.x, obe.y, 0.3),
  ], [ibe.x, ibe.y, obe.x, obe.y]);

  // ============= 3D GEOMETRY =============

  // 3D box mesh: 6 faces from 8 points
  const wallGeometry3D = useMemo(() => {
    if (viewMode !== '3d') return null;

    // 8 vertices as THREE.Vector3
    const v = [
      new THREE.Vector3(ibs.x, ibs.y, ibs.z || 0),  // 0: innerBottomStart
      new THREE.Vector3(ibe.x, ibe.y, ibe.z || 0),  // 1: innerBottomEnd
      new THREE.Vector3(obe.x, obe.y, obe.z || 0),  // 2: outerBottomEnd
      new THREE.Vector3(obs.x, obs.y, obs.z || 0),  // 3: outerBottomStart
      new THREE.Vector3(its.x, its.y, its.z || wall.height),  // 4: innerTopStart
      new THREE.Vector3(ite.x, ite.y, ite.z || wall.height),  // 5: innerTopEnd
      new THREE.Vector3(ote.x, ote.y, ote.z || wall.height),  // 6: outerTopEnd
      new THREE.Vector3(ots.x, ots.y, ots.z || wall.height),  // 7: outerTopStart
    ];

    // 6 faces, each 2 triangles (12 triangles total)
    // Face order: bottom, top, inner, outer, start cap, end cap
    const indices = [
      // Bottom (0,1,2), (0,2,3)
      0, 1, 2,  0, 2, 3,
      // Top (4,7,6), (4,6,5)
      4, 7, 6,  4, 6, 5,
      // Inner face (0,4,5), (0,5,1)
      0, 4, 5,  0, 5, 1,
      // Outer face (3,2,6), (3,6,7)
      3, 2, 6,  3, 6, 7,
      // Start cap (0,3,7), (0,7,4)
      0, 3, 7,  0, 7, 4,
      // End cap (1,5,6), (1,6,2)
      1, 5, 6,  1, 6, 2,
    ];

    const positions = new Float32Array(v.length * 3);
    v.forEach((vec, i) => {
      positions[i * 3] = vec.x;
      positions[i * 3 + 1] = vec.y;
      positions[i * 3 + 2] = vec.z;
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }, [viewMode, ibs, ibe, obs, obe, its, ite, ots, ote, wall.height]);

  // 3D wireframe edges
  const edges3D = useMemo(() => {
    if (viewMode !== '3d') return null;

    const z0 = 0;
    const zH = wall.height;

    return [
      // Bottom edges
      [ibs.x, ibs.y, z0, ibe.x, ibe.y, z0],
      [ibe.x, ibe.y, z0, obe.x, obe.y, z0],
      [obe.x, obe.y, z0, obs.x, obs.y, z0],
      [obs.x, obs.y, z0, ibs.x, ibs.y, z0],
      // Top edges
      [its.x, its.y, zH, ite.x, ite.y, zH],
      [ite.x, ite.y, zH, ote.x, ote.y, zH],
      [ote.x, ote.y, zH, ots.x, ots.y, zH],
      [ots.x, ots.y, zH, its.x, its.y, zH],
      // Verticals
      [ibs.x, ibs.y, z0, its.x, its.y, zH],
      [ibe.x, ibe.y, z0, ite.x, ite.y, zH],
      [obs.x, obs.y, z0, ots.x, ots.y, zH],
      [obe.x, obe.y, z0, ote.x, ote.y, zH],
    ];
  }, [viewMode, ibs, ibe, obs, obe, its, ite, ots, ote, wall.height]);

  // ============= LABELS =============

  const normalAngle = angle + Math.PI / 2;
  const labelOffset = dimensionFontSize * 1.5;
  const labelX = mid.x + Math.cos(normalAngle) * labelOffset;
  const labelY = mid.y + Math.sin(normalAngle) * labelOffset;

  // ============= RENDER =============

  if (viewMode === '3d') {
    const color = getColor();

    return (
      <group>
        {/* Solid volume */}
        {wallGeometry3D && (
          <mesh
            geometry={wallGeometry3D}
            onClick={handleClick}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
          >
            <meshStandardMaterial
              color={color}
              side={THREE.DoubleSide}
              transparent
              opacity={isSelected ? 0.9 : 0.7}
            />
          </mesh>
        )}

        {/* Wireframe edges */}
        {edges3D && edges3D.map((edge, idx) => (
          <line key={`edge-${idx}`}>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array(edge)}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color={isSelected ? '#000000' : '#374151'} linewidth={2} />
          </line>
        ))}

        {/* 8 corner points */}
        {Object.entries(wall.points).map(([name, pt]) => {
          const isInner = name.startsWith('inner');
          const isBottom = name.includes('Bottom');
          const z = isBottom ? 0 : wall.height;
          return (
            <mesh key={name} position={[pt.x, pt.y, z]}>
              <sphereGeometry args={[40, 8, 8]} />
              <meshBasicMaterial color={isInner ? '#3b82f6' : '#ef4444'} />
            </mesh>
          );
        })}

        {/* Dimensions */}
        {showDimensions && (
          <>
            <Text
              position={[mid.x, mid.y, wall.height / 2]}
              fontSize={150}
              color="#374151"
              anchorX="center"
              anchorY="middle"
            >
              {formatLength(length)} m
            </Text>
            <Text
              position={[baseStart.x, baseStart.y, wall.height + 200]}
              fontSize={120}
              color="#ef4444"
              anchorX="center"
              anchorY="bottom"
            >
              {(wall.height / 1000).toFixed(2)} m
            </Text>
          </>
        )}
      </group>
    );
  }

  // ============= 2D VIEW =============
  return (
    <group>
      {/* Filled wall polygon */}
      <mesh
        geometry={wallGeometry2D}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <meshBasicMaterial
          color={getColor()}
          transparent
          opacity={isSelected ? 0.8 : (isHovered ? 0.7 : 0.6)}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Inner edge (bold, dark) */}
      <Line
        points={innerLine2D}
        color="#1f2937"
        lineWidth={isSelected ? 3 : 2}
      />

      {/* Outer edge (lighter) */}
      <Line
        points={outerLine2D}
        color="#6b7280"
        lineWidth={1}
      />

      {/* Start cap */}
      <Line points={startCap2D} color="#9ca3af" lineWidth={1} />

      {/* End cap */}
      <Line points={endCap2D} color="#9ca3af" lineWidth={1} />

      {/* Inner/Outer face indicator (small arrow from mid-inner to mid-outer) */}
      {isSelected && (() => {
        const midInner = midpoint(ibs, ibe);
        const midOuter = midpoint(obs, obe);
        const arrowMid = midpoint(midInner, midOuter);
        return (
          <group>
            {/* "I" label on inner side */}
            <Text
              position={[midInner.x, midInner.y, 3]}
              fontSize={dimensionFontSize * 0.6}
              color="#3b82f6"
              anchorX="center"
              anchorY="middle"
            >
              I
            </Text>
            {/* "A" label on outer side */}
            <Text
              position={[midOuter.x, midOuter.y, 3]}
              fontSize={dimensionFontSize * 0.6}
              color="#ef4444"
              anchorX="center"
              anchorY="middle"
            >
              A
            </Text>
          </group>
        );
      })()}

      {/* Length dimension */}
      {showDimensions && length > 100 && (
        <Text
          position={[labelX, labelY, 2]}
          fontSize={dimensionFontSize}
          color="#374151"
          anchorX="center"
          anchorY="middle"
          rotation={[0, 0, angle > Math.PI / 2 || angle < -Math.PI / 2 ? angle + Math.PI : angle]}
        >
          {formatLength(length)} m
        </Text>
      )}

      {/* Thickness dimension (when selected) */}
      {isSelected && (
        <Text
          position={[
            mid.x + Math.cos(normalAngle) * (wall.thickness / 2),
            mid.y + Math.sin(normalAngle) * (wall.thickness / 2),
            3,
          ]}
          fontSize={dimensionFontSize * 0.7}
          color="#6b7280"
          anchorX="center"
          anchorY="middle"
        >
          {(wall.thickness / 10).toFixed(1)} cm
        </Text>
      )}

      {/* Openings */}
      {(wall.openings || []).map((opening, idx) => (
        <WallVolumeOpening2D
          key={opening.id || idx}
          opening={opening}
          wallId={wall.id}
          wallStart={baseStart}
          wallEnd={baseEnd}
          wallLength={length}
          isSelected={selectedOpeningId === opening.id}
          isHovered={hoveredOpeningId === opening.id}
          onSelect={onSelectOpening}
          onHover={onHoverOpening}
        />
      ))}
    </group>
  );
}

/**
 * Simple 2D opening visualization for the new wall model
 */
function WallVolumeOpening2D({
  opening,
  wallId,
  wallStart,
  wallEnd,
  wallLength,
  isSelected = false,
  isHovered = false,
  onSelect,
  onHover,
}) {
  const { position = 0.5, width = 1000, type = 'window' } = opening;

  const center = {
    x: wallStart.x + (wallEnd.x - wallStart.x) * position,
    y: wallStart.y + (wallEnd.y - wallStart.y) * position,
  };

  const angle = lineAngle(wallStart, wallEnd);
  const halfWidth = width / 2;
  const dx = Math.cos(angle) * halfWidth;
  const dy = Math.sin(angle) * halfWidth;

  const points = useMemo(() => [
    new THREE.Vector3(center.x - dx, center.y - dy, 2),
    new THREE.Vector3(center.x + dx, center.y + dy, 2),
  ], [center.x, center.y, dx, dy]);

  const getColor = () => {
    if (isSelected) return '#3b82f6';
    if (isHovered) return '#60a5fa';
    return type === 'door' ? '#f97316' : '#ffffff';
  };

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect?.(wallId, opening.id);
  }, [wallId, opening.id, onSelect]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.('opening', opening.id);
  }, [opening.id, onHover]);

  const handlePointerOut = useCallback(() => {
    onHover?.(null, null);
  }, [onHover]);

  return (
    <Line
      points={points}
      color={getColor()}
      lineWidth={isSelected ? 10 : (isHovered ? 8 : 6)}
      onClick={handleClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    />
  );
}

export default WallVolume3D;
